/* ============================================================
   KAN-133 (1d) — search box inside a native filter group
   ============================================================
   The Gems filter lists every stone in the collection — 64 of them on
   /collections/bracelets-for-men — so finding "Tiger Eye" means scrolling
   past sixty checkboxes. This adds the same search box the Searchanise
   search page has (assets/searchanise-search.js) to the theme's own
   filters, so the two behave identically wherever a shopper meets them.

   Searchanise's own "show a search bar in a filter" setting never reaches
   the storefront on an API integration, and it would not cover collection
   pages in any case: those run on Shopify's native faceting, which KAN-133
   deliberately left in place.

   Purely a display filter over values Liquid has already rendered —
   facets.liquid writes every value into the page with no "show more"
   truncation, so hiding the non-matching rows can never hide a value the
   shopper could not otherwise reach. Nothing is requested, and the counts
   beside each value stay exactly as the server calculated them.
============================================================ */
(function () {
  if (!window.themeVariables) return;

  // From how many values a group gets a search box: Theme settings > Filters
  // (0 turns it off). 20 when the setting is missing, matching the search
  // page, which mirrors the Searchanise dashboard's "If filter values more
  // than" setting.
  var configured = window.themeVariables.settings && window.themeVariables.settings.facetSearchMinValues;
  var MIN_VALUES = typeof configured === 'number' ? configured : 20;
  if (MIN_VALUES <= 0) return;

  // What is typed into each group's box, so a term survives the section
  // re-render that every filter change triggers. Keyed by the filter's own
  // parameter name plus the panel it is in — the sidebar and the drawer hold
  // separate copies of the same filter.
  var terms = {};

  function groupKey(group) {
    var input = group.querySelector('input[name^="filter."]');
    var context = group.closest('facets-drawer') ? 'drawer' : 'sidebar';

    return input ? context + ':' + input.name : null;
  }

  // The label carries the value's text, and the count after it when the
  // merchant has counts turned on. Both are matched, so typing "17" is
  // harmless rather than surprising, and the text is read once per render.
  function rowText(row) {
    var cached = row.getAttribute('data-filter-value-text');
    if (cached !== null) return cached;

    var label = row.querySelector('label');
    var text = (label ? label.textContent : '').trim().toLowerCase();

    row.setAttribute('data-filter-value-text', text);

    return text;
  }

  function apply(group, term) {
    var rows = group.querySelectorAll('.checkbox-control');
    var matches = 0;

    rows.forEach(function (row) {
      var checkbox = row.querySelector('input[type="checkbox"]');
      // An applied value always stays visible: hiding it would take away the
      // only control that can undo it.
      var keep = !term || (checkbox && checkbox.checked) || rowText(row).indexOf(term) !== -1;

      row.hidden = !keep;
      if (keep) matches++;
    });

    var empty = group.querySelector('[data-filter-value-search-empty]');
    if (empty) empty.hidden = matches > 0;
  }

  function enhance(group) {
    // Either attribute means a box is already there: the Searchanise search
    // page builds its own with a name of its own, and adding a second one to
    // the same group is the bug this guards against.
    if (group.querySelector('[data-filter-value-search], [data-facet-value-search]')) return;

    var rows = group.querySelectorAll('.checkbox-control');
    if (rows.length < MIN_VALUES) return;

    // The values sit in their own wrapper inside the accordion's content;
    // taking it from a row rather than by class keeps this working if the
    // theme changes the wrapper's utility classes.
    var list = rows[0].parentElement;
    if (!list) return;

    var strings = window.themeVariables.strings;
    var title = group.querySelector('.accordion__toggle');
    var placeholder = String(strings.facetsSearchFilterValues || '').replace(
      '__NAME__',
      title ? title.textContent.trim() : ''
    );

    // .input-prefix is the theme's own bordered field wrapper — the one the
    // price inputs sit in — so the box, its radius and its focus ring come
    // from the theme rather than from styling written here.
    var box = document.createElement('label');
    box.className = 'filter-value-search input-prefix text-sm';

    var input = document.createElement('input');
    input.type = 'search';
    input.className = 'field';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.placeholder = placeholder;
    input.setAttribute('aria-label', placeholder);
    input.setAttribute('data-filter-value-search', '');

    box.appendChild(input);
    list.parentNode.insertBefore(box, list);

    var empty = document.createElement('p');
    empty.className = 'text-subdued text-sm';
    empty.textContent = strings.facetsNoFilterValues || '';
    empty.hidden = true;
    empty.setAttribute('data-filter-value-search-empty', '');
    list.parentNode.insertBefore(empty, list.nextSibling);

    input.addEventListener('input', function () {
      var key = groupKey(group);
      if (key) terms[key] = input.value;
      apply(group, input.value.trim().toLowerCase());
    });

    // Put back whatever was typed before the last filter change replaced
    // this panel, so ticking a value does not silently undo the shopper's
    // narrowing of the list they ticked it in.
    var key = groupKey(group);
    var previous = key ? terms[key] : '';

    if (previous) {
      input.value = previous;
      apply(group, previous.trim().toLowerCase());
    }
  }

  function enhanceAll() {
    document.querySelectorAll('.facets accordion-disclosure').forEach(enhance);
  }

  enhanceAll();

  // Applying a filter rebuilds the facets panel and takes the box with it.
  // "facet:update" is the theme's own signal for that and is the fast path,
  // but it is not the last word: a filtered collection page was measured
  // rebuilding the panel a second time about three seconds later, well after
  // the event, which left the box gone. Watching for the rebuild itself
  // covers both passes, and any third party that re-renders the panel.
  //
  // This cannot loop: the box this inserts triggers one more pass, which
  // finds the box already in place and returns without touching anything.
  var pending = null;

  function scheduleEnhance() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      enhanceAll();
    }, 150);
  }

  document.addEventListener('facet:update', scheduleEnhance);

  new MutationObserver(scheduleEnhance).observe(document.body, { childList: true, subtree: true });
})();
